import { createSelector } from 'reselect';
import { selectCurrentAccountId } from './accounts';

const selectInvestmentObjectives = (s) => s.investmentObjectives.items;

const selectPortfolioProposals = (s) => s.investmentObjectives.portfolioProposals;
const selectPortfolioProposalsProperty = (s, property) =>
  s.investmentObjectives[`portfolioProposal${property}`];

const selectPortfolioProposalsProperties = createSelector(
  (s) => selectPortfolioProposalsProperty(s, 'Kpis'),
  (s) => selectPortfolioProposalsProperty(s, 'Holdings'),
  (s) => selectPortfolioProposalsProperty(s, 'SectorAllocation'),
  (s) => selectPortfolioProposalsProperty(s, 'GeoAllocation'),
  (s) => selectPortfolioProposalsProperty(s, 'AssetClassAllocation'),
  (s) => selectPortfolioProposalsProperty(s, 'Currencies'),
  (s) => selectPortfolioProposalsProperty(s, 'Performances'),
  (s) => selectPortfolioProposalsProperty(s, 'MaxDrawdowns'),
  (
    kpis,
    holdings,
    sectorAllocation,
    geoAllocation,
    assetClassAllocation,
    currencies,
    performances,
    maxDrawdowns
  ) => ({
    kpis,
    holdings,
    sectorAllocation,
    geoAllocation,
    assetClassAllocation,
    currencies,
    performances,
    maxDrawdowns,
  })
);

function getPortfolioProposalProperties(id, properties) {
  return {
    kpis: properties.kpis[id],
    holdings: properties.holdings[id],
    sectorAllocation: properties.sectorAllocation[id],
    geoAllocation: properties.geoAllocation[id],
    assetClassAllocation: properties.assetClassAllocation[id],
    currencies: properties.currencies[id],
    performanceChartComparisons: properties.performances[id]?.comparisons,
    performanceChartBenchmarkId: properties.performances[id]?.performanceChartBenchmarkId,
    performanceChart: properties.performances[id]?.performanceChart,
    performanceChartYearsTag: properties.performances[id]?.performanceChartYearsTag,
    maxDrawdown: properties.maxDrawdowns[id],
  };
}

function getPortfolioProposalsComplete(id, portfolioProposals, properties) {
  if (!portfolioProposals[id]) return undefined;
  return portfolioProposals[id]?.map((pp) => ({
    ...pp,
    ...getPortfolioProposalProperties(pp.id, properties),
  }));
}

const selectPortfolios = (s) => s.investmentObjectives.portfolios;
const selectPortfoliosProperty = (s, property) => s.investmentObjectives[`portfolio${property}`];

const selectPortfolioProperties = createSelector(
  (s) => selectPortfoliosProperty(s, 'Holdings'),
  (s) => selectPortfoliosProperty(s, 'SectorAllocation'),
  (s) => selectPortfoliosProperty(s, 'GeoAllocation'),
  (s) => selectPortfoliosProperty(s, 'AssetClassAllocation'),
  (s) => selectPortfoliosProperty(s, 'Currencies'),
  (s) => selectPortfoliosProperty(s, 'Performances'),
  (s) => selectPortfoliosProperty(s, 'Kpis'),
  (
    holdings,
    sectorAllocation,
    geoAllocation,
    assetClassAllocation,
    currencies,
    performances,
    kpis
  ) => ({
    holdings,
    sectorAllocation,
    geoAllocation,
    assetClassAllocation,
    currencies,
    performances,
    kpis,
  })
);

function getPortfolioProperties(id, properties) {
  return {
    holdings: properties.holdings[id],
    sectorAllocation: properties.sectorAllocation[id],
    geoAllocation: properties.geoAllocation[id],
    assetClassAllocation: properties.assetClassAllocation[id],
    currencies: properties.currencies[id],
    performanceChartCompare: properties.performances[id]?.compare,
    performanceChartBenchmarkId: properties.performances[id]?.performanceChartBenchmarkId,
    performanceChart: properties.performances[id]?.performanceChart,
    performanceChartYearsTag: properties.performances[id]?.performanceChartYearsTag,
    kpis: properties.kpis[id]?.kpis,
  };
}

function getPortfolioComplete(id, portfolios, properties) {
  if (!portfolios[id]) return undefined;
  return {
    ...portfolios[id],
    ...getPortfolioProperties(portfolios[id].id, properties),
  };
}

export const selectCurrentAccountInvestmentObjectives = createSelector(
  selectCurrentAccountId,
  selectInvestmentObjectives,
  selectPortfolioProposals,
  selectPortfolioProposalsProperties,
  selectPortfolios,
  selectPortfolioProperties,
  (
    accountId,
    objectives,
    portfolioProposals,
    portfolioProposalsProperties,
    portfolios,
    portfolioProperties
  ) => {
    if (accountId && objectives[accountId]) {
      return objectives[accountId].map((o) => ({
        ...o,
        portfolio: getPortfolioComplete(o.id, portfolios, portfolioProperties),
        portfolioProposals: getPortfolioProposalsComplete(
          o.id,
          portfolioProposals,
          portfolioProposalsProperties
        ),
      }));
    }

    return undefined;
  }
);
